/**
 * Loadout rules shared by the hangar, the API, the queue and the room: what may be equipped on
 * which mech, what the account owns or has unlocked, and how the choices resolve into the
 * livery the renderer draws. `validateLoadout` explains every problem; `sanitizeLoadout` drops
 * the offenders in a fixed order so two callers with the same input agree on the result.
 */
import { COSMETICS, getMech, MECHS, MODULES, } from '../data/index.js';
import { defaultLoadout } from './schemas.js';
const PAINT_FIELDS = ['primary', 'secondary', 'accent'];
/** Free items belong to everyone; level items unlock by level; the rest need a purchase row. */
export function cosmeticUnlocked(item, ownership) {
    if (item.levelUnlock !== null)
        return ownership.level >= item.levelUnlock;
    if (item.price === 0)
        return true;
    return ownership.cosmetics.has(item.id);
}
export function moduleOwned(id, ownership) {
    return ownership.modules.has(id);
}
function checkCosmetic(field, id, kind, mechId, ownership, issues) {
    const item = COSMETICS[id];
    if (!item) {
        issues.push({ code: 'unknown', field, id, message: `unknown cosmetic "${id}"` });
        return false;
    }
    if (item.kind !== kind) {
        issues.push({ code: 'kind', field, id, message: `"${id}" is a ${item.kind}, not a ${kind}` });
        return false;
    }
    if (item.kind === 'part' && item.chassis !== mechId) {
        issues.push({ code: 'chassis', field, id, message: `"${id}" does not fit ${mechId}` });
        return false;
    }
    if (ownership && !cosmeticUnlocked(item, ownership)) {
        const locked = item.levelUnlock !== null;
        issues.push({
            code: locked ? 'levelLocked' : 'unowned',
            field,
            id,
            message: locked
                ? `"${id}" unlocks at level ${String(item.levelUnlock)}`
                : `"${id}" is not owned`,
        });
        return false;
    }
    return true;
}
/**
 * Every problem with a loadout for `mechId`. Pass `ownership: null` to check against the
 * catalog only (the room does this: it must never refuse a player over a hangar mistake).
 */
export function validateLoadout(mechId, loadout, ownership) {
    const issues = [];
    const def = MECHS[mechId];
    if (!def) {
        issues.push({
            code: 'unknown',
            field: 'modules',
            id: mechId,
            message: `unknown mech "${mechId}"`,
        });
        return issues;
    }
    const seenModules = new Set();
    const seenGroups = new Set();
    let modules = 0;
    for (const id of loadout.modules) {
        const module = MODULES[id];
        if (!module) {
            issues.push({ code: 'unknown', field: 'modules', id, message: `unknown module "${id}"` });
            continue;
        }
        if (seenModules.has(id)) {
            issues.push({
                code: 'duplicate',
                field: 'modules',
                id,
                message: `"${id}" is equipped twice`,
            });
            continue;
        }
        seenModules.add(id);
        if (seenGroups.has(module.group)) {
            issues.push({
                code: 'group',
                field: 'modules',
                id,
                message: `"${id}" shares its ${module.group} group with another module`,
            });
            continue;
        }
        seenGroups.add(module.group);
        if (ownership && !moduleOwned(id, ownership)) {
            issues.push({ code: 'unowned', field: 'modules', id, message: `"${id}" is not owned` });
            continue;
        }
        modules++;
    }
    if (modules > def.slots.module) {
        issues.push({
            code: 'tooMany',
            field: 'modules',
            id: null,
            message: `${def.name} has ${String(def.slots.module)} module slots`,
        });
    }
    const c = loadout.cosmetics;
    for (const field of PAINT_FIELDS) {
        const id = c[field];
        if (id !== null)
            checkCosmetic(field, id, 'paint', mechId, ownership, issues);
    }
    if (c.emissive !== null)
        checkCosmetic('emissive', c.emissive, 'emissive', mechId, ownership, issues);
    if (c.decal !== null)
        checkCosmetic('decal', c.decal, 'decal', mechId, ownership, issues);
    const seenParts = new Set();
    let parts = 0;
    for (const id of c.parts) {
        if (seenParts.has(id)) {
            issues.push({ code: 'duplicate', field: 'parts', id, message: `"${id}" is equipped twice` });
            continue;
        }
        seenParts.add(id);
        if (checkCosmetic('parts', id, 'part', mechId, ownership, issues))
            parts++;
    }
    if (parts > def.slots.cosmetic) {
        issues.push({
            code: 'tooMany',
            field: 'parts',
            id: null,
            message: `${def.name} has ${String(def.slots.cosmetic)} cosmetic slots`,
        });
    }
    const primary = loadout.weapons.primary;
    if (primary !== null && !weaponFits(def.alternates.primary, primary)) {
        issues.push({
            code: 'kind',
            field: 'weapon',
            id: primary,
            message: `"${primary}" is not an alternate primary for ${def.name}`,
        });
    }
    return issues;
}
/** An alternate primary must be one the mech's data lists (a shipped weapon with a model). */
export function weaponFits(alternates, weaponId) {
    return alternates.includes(weaponId);
}
/**
 * The largest valid loadout inside `loadout`: offenders are dropped in list order, and lists
 * over their slot count are cut from the end. Deterministic, never throws; an unknown mech
 * yields the default loadout.
 */
export function sanitizeLoadout(mechId, loadout, ownership) {
    const def = MECHS[mechId];
    if (!def)
        return defaultLoadout();
    const modules = [];
    const groups = new Set();
    for (const id of loadout.modules) {
        const module = MODULES[id];
        if (!module || modules.includes(id) || groups.has(module.group))
            continue;
        if (ownership && !moduleOwned(id, ownership))
            continue;
        if (modules.length >= def.slots.module)
            break;
        modules.push(id);
        groups.add(module.group);
    }
    const keep = (field, id, kind) => id !== null && checkCosmetic(field, id, kind, mechId, ownership, []) ? id : null;
    const parts = [];
    for (const id of loadout.cosmetics.parts) {
        if (parts.includes(id) || !checkCosmetic('parts', id, 'part', mechId, ownership, []))
            continue;
        if (parts.length >= def.slots.cosmetic)
            break;
        parts.push(id);
    }
    const primary = loadout.weapons.primary;
    return {
        modules,
        weapons: {
            primary: primary !== null && weaponFits(def.alternates.primary, primary) ? primary : null,
        },
        cosmetics: {
            primary: keep('primary', loadout.cosmetics.primary, 'paint'),
            secondary: keep('secondary', loadout.cosmetics.secondary, 'paint'),
            accent: keep('accent', loadout.cosmetics.accent, 'paint'),
            emissive: keep('emissive', loadout.cosmetics.emissive, 'emissive'),
            decal: keep('decal', loadout.cosmetics.decal, 'decal'),
            parts,
        },
    };
}
function hexOf(id, kind) {
    if (id === null)
        return null;
    const item = COSMETICS[id];
    return item && item.kind === kind ? item.hex : null;
}
/**
 * The livery the renderer draws: the loadout's choices over the mech's stock livery `base`
 * (from its asset sidecar). A decal is tinted with the accent so it reads on any paint.
 */
export function liveryOf(base, loadout) {
    const c = loadout.cosmetics;
    const accent = hexOf(c.accent, 'paint') ?? base.accent;
    const decalItem = c.decal !== null ? COSMETICS[c.decal] : undefined;
    return {
        primary: hexOf(c.primary, 'paint') ?? base.primary,
        secondary: hexOf(c.secondary, 'paint') ?? base.secondary,
        accent,
        emissive: hexOf(c.emissive, 'emissive') ?? base.emissive,
        wear: base.wear,
        decal: decalItem && decalItem.kind === 'decal' ? { id: decalItem.tile, tint: accent } : base.decal,
    };
}
/** The kit part ids to show: the mech's stock cosmetics plus the equipped premium parts. */
export function partsOf(stockParts, loadout) {
    const parts = new Set(stockParts);
    for (const id of loadout.cosmetics.parts) {
        const item = COSMETICS[id];
        if (item && item.kind === 'part')
            parts.add(item.part);
    }
    return [...parts];
}
/** Whether a mech id names a roster mech (for callers that only have a string). */
export function isRosterMech(mechId) {
    try {
        getMech(mechId);
        return true;
    }
    catch {
        return false;
    }
}
//# sourceMappingURL=loadout.js.map